import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { MenuItem } from "../../../Types/Layout/SidebarType";
import { menuList } from "../../../Data/LayoutData/SidebarData";
import { IUserMenusList } from "../../../Types/UserProfileDetails";
import { buildSidebarMenuFromApi } from "../../../utils/sidebarMenu";

interface MenuState {
  filteredMenu: MenuItem[];
  shouldRefreshMenu: boolean;
}

const initialState: MenuState = {
  filteredMenu: [],
  shouldRefreshMenu: false,
};

const allowedUserIdsForCallingSystemList = [52, 377];
const allowedUserIdsForInventoryList = [1, 1178, 377, 796, 309];

// Thunk to filter menu based on user role
export const filterMenuThunk = createAsyncThunk(
  "menu/filter",
  async ({
    user,
    userMenus,
  }: {
    user: any;
    userMenus?: IUserMenusList[];
  }) => {
    const userRole = user?.user_type?.toLowerCase()?.replaceAll(" ", "") ?? "";
    const userId = Number(user?.user_id);
    const sourceMenus = Array.isArray(userMenus)
      ? buildSidebarMenuFromApi(userMenus)
      : menuList;

    const filterMenuItems = (menuItems: MenuItem[]): MenuItem[] => {
      if (Number(user?.role) === 111) {
        return menuItems.map((menu) => ({
          ...menu,
          Items: menu?.Items?.filter(
            (menuItem) => menuItem.title === "Dashboard"
          ),
        }));
      }

      // Remove petty cash if not allowed
      if (user?.petty_cash?.toLowerCase() !== "yes") {
        menuItems = menuItems.map((menu) => ({
          ...menu,
          children: menu.children?.filter(
            (child) =>
              child.title?.toLowerCase().replaceAll(" ", "") !== "pettycash"
          ),
        }));
      }

      return menuItems
        .filter((menuItem) => {
          // recurse into nested Items if present
          if (menuItem.Items) {
            menuItem.Items = filterMenuItems(menuItem.Items);
          }

          const isAdminRole = userRole === "admin";

          // special rule for Calling System List (id = 12):
          // show if (admin) OR (userId in allowed list)
          const isAllowedCallingSystem =
            menuItem.id === 12 &&
            (isAdminRole ||
              allowedUserIdsForCallingSystemList.includes(userId));

          const isAllowedInventorySystem =
            menuItem.id === 8 &&
            allowedUserIdsForInventoryList.includes(userId);

          // normal role-based visibility (case-insensitive)
          const normalizedRoles = (menuItem.roles ?? []).map((r) =>
            r?.toLowerCase().replaceAll(" ", "")
          );
          const isMenuVisibleByRole = normalizedRoles.includes(userRole);

          // final visibility for this item
          const isMenuVisible =
            isMenuVisibleByRole ||
            isAllowedCallingSystem ||
            isAllowedInventorySystem;

          // keep if visible OR has visible nested Items
          return isMenuVisible || (menuItem.Items && menuItem.Items.length > 0);
        })
        .map((menuItem) => {
          if (menuItem.Items) {
            const normalizedUserRole = userRole;
            const isAdminRole = userRole === "admin";

            menuItem.Items = menuItem.Items.filter((child) => {
              const childRoles = (child.roles ?? []).map((r) =>
                r?.toLowerCase().replaceAll(" ", "")
              );
              const childVisibleByRole =
                childRoles.includes(normalizedUserRole);

              // special case for Calling System List (id = 12)
              const childIsAllowedCallingSystem =
                child.id === 12 &&
                (isAdminRole ||
                  allowedUserIdsForCallingSystemList.includes(userId));

              const childIsAllowedInventorySystem =
                child.id === 8 &&
                (isAdminRole ||
                  allowedUserIdsForInventoryList.includes(userId));

              return childVisibleByRole || childIsAllowedCallingSystem || childIsAllowedInventorySystem;
            });
          }
          return menuItem;
        });
    };

    return Array.isArray(userMenus) ? sourceMenus : filterMenuItems(sourceMenus);
  }
);

const menuSlice = createSlice({
  name: "menu",
  initialState,
  reducers: {
    resetMenu: (state) => {
      state.filteredMenu = [];
    },
    triggerMenuRefresh: (state) => {
      state.shouldRefreshMenu = !state.shouldRefreshMenu;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      filterMenuThunk.fulfilled,
      (state, action: PayloadAction<MenuItem[]>) => {
        state.filteredMenu = action.payload;
      }
    );
  },
});

export const { resetMenu, triggerMenuRefresh } = menuSlice.actions;
export default menuSlice.reducer;
